import React, { useState, useEffect } from "react";
import { Activity } from "lucide-react";
import { useAlert } from "../../../hooks/useAlert";
import { apiClient } from "../../../utils/apiClient";
import { AuditLogEntry, AuditLogPagination } from "../../../types/audit";
import Pagination from "../../../components/common/ui/pagination/Pagination";
import ActivityLogItem from "../../../components/common/activity/ActivityLogItem";

interface ActivityTabProps {
  caseId: string;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({ caseId }) => {
  const { setAlert } = useAlert();
  const [activities, setActivities] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<AuditLogPagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadActivities();
  }, [caseId, filter, currentPage]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAuditLogs({
        caseId,
        resourceType: filter !== "all" ? filter : undefined,
        page: currentPage,
        limit: 20,
      });

      setActivities(response.logs);
      setPagination(response.pagination);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to load activity logs",
      });
      setActivities([]);
      setPagination({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-4">
      {/* Header with Filter */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-black dark:text-white">Activity Log</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            View all actions and changes related to this case
          </p>
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-9 rounded-lg border border-gray-300 bg-transparent px-3 py-1 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        >
          <option value="all">All Activity</option>
          <option value="file">Files Only</option>
          <option value="form">Forms Only</option>
          <option value="user">User Actions</option>
          <option value="folder">Folders Only</option>
        </select>
      </div>

      {/* Activity Timeline */}
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center p-8">
            <Activity size={48} className="mb-4 text-gray-300 dark:text-gray-700" />
            <p className="text-gray-500 dark:text-gray-400">No activity yet</p>
            <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
              Activity will appear here as actions are performed on this case
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 p-6 dark:divide-gray-800">
            {activities.map((entry) => (
              <ActivityLogItem
                key={entry.id}
                entry={entry}
                variant="default"
                showMetadata={true}
                showChanges={true}
                showIpAddress={true}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && activities.length > 0 && pagination.totalPages > 1 && (
          <div className="p-6 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.limit}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTab;
